/**
 * Elasticsearch document ids are built from request input (`geoId`, linkspace/breadcrumb `id`,
 * accommodation slugs...) and end up as a URL path segment (`<index>/_doc/<id>`). Every id we store
 * is made of letters, digits, `_` and `-` (`123`, `city-123-63`, `poi_NOC_154907`, `hero`), so
 * anything else - `/`, `.`, `%`, `?`, `#` - is rejected: a `../../<other-index>/_doc/<id>` value
 * would otherwise read any document of any index the ES user can access (path traversal).
 */
const ES_DOCUMENT_ID_RE = /^[A-Za-z0-9_-]+$/;

export const isValidEsDocumentId = (id: unknown): id is number | string =>
    (typeof id === 'string' || typeof id === 'number') && ES_DOCUMENT_ID_RE.test(String(id));

export class InvalidEsDocumentIdError extends Error {
    constructor() {
        super('Invalid Elasticsearch document id');
        this.name = 'InvalidEsDocumentIdError';
    }
}

/** The id as a safe path segment - throws for anything that is not a plain document id. */
export const toEsDocumentIdSegment = (id: unknown): string => {
    if (!isValidEsDocumentId(id)) {
        throw new InvalidEsDocumentIdError();
    }

    return encodeURIComponent(String(id));
};
